import React, { useMemo, useEffect } from "react";
import { BOTTLE_MENU } from "@/lib/bottles";
import { MenuImages } from "@/components/MenuImages";

/**
 * Soho69 — Standalone in-venue drink menu.
 *
 * Route: `/menu`  (bare layout — no header/footer)
 * Mobile-first · Neon-pink on black.
 *
 * The list is driven ENTIRELY by the site's single source of truth
 * (`bottles.js` → BOTTLE_MENU), the same catalogue used on /tables and
 * /book for VIP bottle add-ons. No extra categories, no placeholder
 * cocktails, no invented items — /menu mirrors what the club actually
 * sells on the rest of the site.
 */

const CATEGORY_LABELS = {
  vodka: "Vodka",
  gin: "Gin",
  brandy: "Brandy & Cognac",
  whiskey: "Whiskey",
  rum: "Rum",
  champagne: "Champagne",
};

function PriceRow({ name, price }) {
  return (
    <div
      className="flex items-baseline justify-between gap-4 py-3 border-b border-neon/15 last:border-b-0"
      data-testid="menu-item-row"
    >
      <span className="text-white/90 text-[15px] md:text-base leading-tight">
        {name}
      </span>
      <span className="font-heading text-lg md:text-xl text-neon whitespace-nowrap">
        €{price}
      </span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="mt-8 first:mt-2 mb-2 text-[10px] uppercase tracking-[0.32em] text-white/40 font-bold">
      {children}
    </div>
  );
}

export default function MenuPage() {
  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = "#000";
    document.title = "Soho69 · Menu";
    return () => {
      document.body.style.background = prev;
    };
  }, []);

  const sections = useMemo(
    () =>
      BOTTLE_MENU.map((cat) => ({
        label: CATEGORY_LABELS[cat.catKey] || cat.catKey,
        items: cat.items,
      })),
    []
  );

  return (
    <div
      data-testid="menu-page"
      className="min-h-screen w-full bg-black text-white pb-16"
      style={{
        backgroundImage:
          "radial-gradient(1200px 600px at 50% -10%, rgba(255,27,141,0.18), transparent 60%)",
      }}
    >
      {/* Header — brush stamp */}
      <div className="pt-8 md:pt-12 pb-6 text-center px-4">
        <div className="text-[10px] uppercase tracking-[0.42em] text-neon font-bold">
          Tenerife
        </div>
        <h1
          className="font-heading text-5xl md:text-7xl uppercase mt-3 leading-[0.95]"
          data-testid="menu-title"
        >
          SOHO <span className="italic font-display text-neon">69</span>
        </h1>
        <p className="mt-2 text-[11px] uppercase tracking-[0.32em] text-white/40">
          Bottle Menu
        </p>
      </div>

      <MenuImages />

      {/* Grouped bottle list */}
      <div
        className="max-w-3xl mx-auto px-4 md:px-8 mt-6"
        data-testid="menu-panel"
      >
        {sections.map((sec) => (
          <div key={sec.label} data-testid={`menu-section-${sec.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <SectionTitle>{sec.label}</SectionTitle>
            <div>
              {sec.items.map((it) => (
                <PriceRow key={it.id} name={it.name} price={it.price} />
              ))}
            </div>
          </div>
        ))}
        <p className="mt-8 text-[10px] uppercase tracking-[0.28em] text-white/40 text-center">
          Bottles served with premium mixers · Ask staff for VIP table upgrades
        </p>
      </div>

      {/* Footer brush stamp */}
      <div className="mt-14 text-center">
        <div
          className="inline-block px-8 py-2 font-heading text-xl uppercase tracking-[0.24em]"
          style={{
            background: "linear-gradient(135deg,#FF1B8D 0%,#FF6BC6 100%)",
            color: "#000",
            transform: "skew(-8deg)",
          }}
        >
          Soho 69
        </div>
        <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-white/40">
          Tenerife
        </div>
      </div>
    </div>
  );
}
